import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Ban, ShieldAlert, LogOut, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SuspensionScreen({ user }) {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("ban");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const banned = user?.account_status === "banned";
  const submit = async () => {
    if (!message.trim()) return toast.error("Please explain your appeal");
    setBusy(true);
    try {
      await api.post("/appeals", { reason, message: message.trim(), contact_email: email.trim() });
      setSubmitted(true);
      toast.success("Appeal submitted. Our team will review within 3 business days.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="suspension-screen" className="min-h-screen bg-[#0A0605] flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-3xl border border-red-500/30 bg-gradient-to-br from-[#180D0D] to-[#0A0605] p-6 space-y-4">
        <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center text-red-400 mx-auto">
          {banned ? <Ban size={32}/> : <ShieldAlert size={32}/>}
        </div>
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-white">
            Account {banned ? "Banned" : "Suspended"}
          </h1>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
            {banned
              ? "Your account has been permanently banned for violating our community guidelines."
              : "Your account is temporarily suspended pending review."}
          </p>
          {user?.moderation_reason && (
            <div className="mt-3 rounded-xl border border-red-500/20 bg-red-500/5 p-3 text-left">
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Reason</p>
              <p className="text-sm text-zinc-200 mt-1">{user.moderation_reason}</p>
            </div>
          )}
        </div>

        {!open && !submitted && (
          <>
            <Button data-testid="open-appeal-btn" onClick={() => setOpen(true)} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
              Submit Appeal
            </Button>
            <Button data-testid="logout-btn" onClick={logout} className="w-full h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-sm">
              <LogOut size={14} className="mr-1"/> Log out
            </Button>
          </>
        )}

        {open && !submitted && (
          <div data-testid="appeal-form" className="space-y-3 pt-2">
            <p className="text-xs text-zinc-400">Please tell us why you believe this decision should be reviewed.</p>
            <div className="flex gap-2 flex-wrap">
              {[{k: "ban", l: "Ban review"}, {k: "suspension", l: "Suspension"}, {k: "content_removal", l: "Content removed"}, {k: "other", l: "Other"}].map(o => (
                <button key={o.k} data-testid={`appeal-reason-${o.k}`} onClick={() => setReason(o.k)} className={`h-8 px-3 rounded-full text-[11px] font-medium border ${reason === o.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{o.l}</button>
              ))}
            </div>
            <Input data-testid="appeal-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Contact email (optional)" className="bg-zinc-900 border-zinc-800"/>
            <Textarea data-testid="appeal-message" value={message} onChange={e => setMessage(e.target.value)} placeholder="Explain the situation in detail..." rows={5} className="bg-zinc-900 border-zinc-800"/>
            <div className="flex gap-2">
              <Button data-testid="appeal-cancel" onClick={() => setOpen(false)} className="flex-1 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-sm">Cancel</Button>
              <Button data-testid="appeal-submit" onClick={submit} disabled={busy || !message.trim()} className="flex-1 h-10 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-sm font-semibold">
                {busy ? <Loader2 className="animate-spin mr-1" size={14}/> : null} Submit
              </Button>
            </div>
          </div>
        )}

        {submitted && (
          <div data-testid="appeal-submitted" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center space-y-2">
            <CheckCircle2 size={32} className="text-emerald-400 mx-auto"/>
            <p className="text-sm text-white font-semibold">Appeal received</p>
            <p className="text-[11px] text-zinc-400">Our team will respond via email within 3 business days.</p>
            <Button data-testid="logout-after-appeal" onClick={logout} className="w-full h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs mt-2">
              <LogOut size={12} className="mr-1"/> Log out
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
